/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/member-ordering */
import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {AppThemeState, State} from '@ansys-internal/awf-angular/store';
import {AWCTableColumns, AWCTableRow,} from '@ansys-internal/awc-angular/tables';
import {PlotState} from '../../state/lib/plot.state';
import {ActionContributionsService} from '@ansys-internal/awf-angular/contributions';
import {Layout} from 'plotly.js';
import {DefaultChartAxisColourConfig} from '../../actions';

@Injectable({
  providedIn: 'root',
})
export class PlotService {
  /**
   * Subject to subscribe to graph data
   */
  private pointIndex = new BehaviorSubject<string[]>(['']);
  private tableData = new BehaviorSubject<
    [AWCTableColumns[], AWCTableRow[], string[], string[]]
  >([[], [], [], []]);
  public activePointIndex = this.pointIndex.asObservable();
  public activeTableData = this.tableData.asObservable();

  constructor(
    private state: State,
    private actions: ActionContributionsService
  ) {}

  /**
   * Set graph data
   */
  public setGraphData(data: any): void {
    this.state.set(PlotState, {
      ...this.state.value(PlotState),
      data,
    });
  }

  public async resetGraphLayout(): Promise<void> {
    this.state.reset(PlotState);
  }

  public async clearGraph(): Promise<void> {
    await this.resetGraphLayout();
    this.setGraphData([]);
    this.state.reset(PlotState);
  }

  public getYAxisKeys(layout: Partial<Layout>): string[] {
    return Object.keys(layout).filter(key => {
      if (!/^yaxis\d*$/.test(key)) {
        return false;
      }
      const axis = (layout as any)[key];
      return this.isValidAxisRange(axis?.range);
    });
  }

  protected isValidAxisRange(range: number[]): boolean {
    return (
      Array.isArray(range) &&
      range.length === 2 &&
      isFinite(range[0]) &&
      isFinite(range[1]) &&
      !(range[0] === 0 && range[1] === 0)
    );
  }

  /**
   * Remove all numbered yaxis properties (yaxis2, yaxis3, ...) from a layout object.
   * Keeps xaxis, yaxis, and all other non-yaxisN properties.
   */
  public async stripExtraYAxes(): Promise<void> {
    const currentPlotState = { ...this.state.value(PlotState) };
    const layout = currentPlotState.layout ?? {};   // ensure it's always an object

    const cleaned: Partial<Layout> = {};

    for (const [key, value] of Object.entries(layout)) {
      // Keep everything except yaxisN (where N is a number > 1)
      if (!/^yaxis\d+$/.test(key)) {
        cleaned[key as keyof Layout] = value as any;
      }
    }

    currentPlotState.layout = cleaned;

    this.state.set(PlotState, currentPlotState);
    setTimeout(() => {
      this.state.value(PlotState);
    }, 1000);
  }

  public async removeGraphLayoutAxis(axisKey: string): Promise<void> {
    const currentPlotState = this.state.value(PlotState);

    // clone layout separately so we don’t mutate the original
    const updatedLayout = { ...currentPlotState.layout };
    delete (updatedLayout as any)[axisKey];

    // set new state with updated layout
    this.state.set(PlotState, {
      ...currentPlotState,
      layout: updatedLayout,
    });
  }

  public async updateGraphLayout(layout: Partial<Layout>): Promise<void> {
    const theme = this.state.value(AppThemeState);
    const isDarkMode =
      theme === 'dark' ||
      (theme === 'auto' &&
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches);

    this.state.set(PlotState, {
      ...this.state.value(PlotState),
      layout: {
        ...this.state.value(PlotState).layout,
        ...(await this.actions.execute(DefaultChartAxisColourConfig)),
        ...layout,
        legend: {
          ...this.state.value(PlotState).layout?.legend,
          font: { color: isDarkMode ? '#fff' : '#000' },
          ...layout.legend,
        },
        xaxis: {
          ...this.state.value(PlotState).layout?.xaxis,
          ...(await this.actions.execute(DefaultChartAxisColourConfig)),
          ...layout.xaxis,
        },
        yaxis: {
          ...this.state.value(PlotState).layout?.yaxis,
          ...(await this.actions.execute(DefaultChartAxisColourConfig)),
          ...layout.yaxis,
        },
        yaxis2: {
          ...this.state.value(PlotState).layout?.yaxis2,
          title: { font: { color: 'rgb(33, 150, 243)' } },
          tickfont: { color: 'rgb(33, 150, 243)' },
          gridcolor: 'rgb(33, 150, 243)',
          gridwidth: 0.5,
          color: 'rgb(33, 150, 243)',
          ...layout.yaxis2,
        },
      },
    });
  }

  public updateAxisLimit(axis: string, value: number): void {
    const currentLayout = this.state.value(PlotState)?.layout ?? {};
    const updatedLayout: Partial<Layout> = { ...currentLayout };

    if (axis === 'xMin' || axis === 'xMax') {
      const xRange = [...(currentLayout.xaxis?.range ?? [0, 0])];
      if (axis === 'xMin') xRange[0] = value;
      if (axis === 'xMax') xRange[1] = value;
      updatedLayout.xaxis = { ...currentLayout.xaxis, range: xRange };
    }

    const match = axis.match(/^y(\d*)(Min|Max)$/);
    if (match) {
      const axisNum = match[1]; // '' for yaxis, '2' for yaxis2, etc.
      const boundType = match[2]; // 'Min' or 'Max'
      const axisKey = axisNum === '' ? 'yaxis' : `yaxis${axisNum}`;

      const axisLayout = currentLayout[axisKey as keyof Layout];
      const isAxisLayout = (val: any): val is { range?: [number, number] } =>
        val && typeof val === 'object' && 'range' in val;

      const range = isAxisLayout(axisLayout) ? [...(axisLayout.range ?? [0, 0])] : [0, 0];
      if (axis.endsWith('Min')) range[0] = value;
      if (axis.endsWith('Max')) range[1] = value;

      (updatedLayout as any)[axisKey] = isAxisLayout(axisLayout)
        ? {...axisLayout, range}
        : {range};
    }

    this.updateGraphLayout(updatedLayout);
  }

  /**
   * Extracts a simplified version of the graph layout, keeping only the relevant properties
   * we care about (e.g. axis ranges) and stripping out any extra properties added by Plotly.   * @param layout
   * @protected
   */
  public extractGraphLayout(layout: Partial<Layout>): Partial<Layout> {
    return {
      xaxis: layout.xaxis
        ? {
            range: layout.xaxis.range,
            autorange: layout.xaxis.autorange,
          }
        : undefined,
      yaxis: layout.yaxis
        ? {
            range: layout.yaxis.range,
            autorange: layout.yaxis.autorange,
          }
        : undefined,
      yaxis2: layout.yaxis2
        ? {
            range: layout.yaxis2.range,
            autorange: layout.yaxis2.autorange,
          }
        : undefined,
      legend: layout.legend
        ? {
            orientation: layout.legend.orientation,
          }
        : undefined,
    };
  }

  public setTableData(
    data: [AWCTableColumns[], AWCTableRow[], string[], string[]]
  ): void {
    this.setSelectedPointIndex(data[3]);
    this.tableData.next(data);
  }

  public setSelectedPointIndex(pointIndex: string[]): void {
    this.pointIndex.next(pointIndex);
  }
}
